/**
 * The trial phase as the screens show it (backlog h4, 2026-10-22).
 *
 * The Protocol Intelligence page showed the model's raw reading
 * (`analysis.studyDesign.phase`, "Phase 1" on the Penn digital-health study,
 * which has no drug), and the CRF Builder put the same value into the protocol
 * text it builds, while every generated document said "Not applicable".
 *
 * This file does NOT have its own phase rules. It calls the backend resolver
 * itself (functions/src/studyProfile/storedTrialPhase.ts, the same import
 * pattern as components/crf/seedVisitAssignments.ts), so a screen and a
 * document cannot disagree, and a study analysed before any new stored field
 * gets the same answer as a new one. The answer is computed when the page
 * reads it, so a later change in Study Design or Study Settings shows at once.
 *
 * Pure.
 */
import { storedTrialPhaseFact } from '../../../functions/src/studyProfile/storedTrialPhase';
import { normaliseTrialPhase, type PhaseFact } from '../../../functions/src/studyProfile/trialPhase';

export interface PhaseDisplayInputs {
  /** protocolAnalysis/latest.analysis */
  analysis?: unknown;
  /** projects/{id} (its studySetup carries the product type from Study Settings) */
  projectData?: unknown;
  /** studyDesign/config */
  studyDesign?: unknown;
}

export interface PhaseDisplay {
  /** What the Phase row shows. */
  label: string;
  /** The sponsor-review note the documents carry, or why the phase is not known; null when there is nothing to say. */
  note: string | null;
}

/** The backend's bare "needed" marker, which says nothing a reader can act on. */
const BARE_NEEDED = 'Study phase';

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : {});

/**
 * The resolver's answer, never throwing (round 2): unexpected stored data (a
 * null inside trialPhaseConflict, say) must not take the page down. The
 * fallback is the analysis's own phase in its one written form, with no note.
 */
function safeFact(i: PhaseDisplayInputs): PhaseFact {
  try {
    return storedTrialPhaseFact({ analysis: i.analysis, projectData: i.projectData, studyDesign: i.studyDesign });
  } catch (err) {
    console.warn('[protocolPhaseDisplay] trial phase could not be resolved; showing the analysis phase', err);
    return { value: normaliseTrialPhase(obj(obj(i.analysis).studyDesign).phase), source: null };
  }
}

export function protocolPhaseDisplay(i: PhaseDisplayInputs): PhaseDisplay {
  const fact = safeFact(i);
  const note = fact.review ?? (fact.value === null && fact.needed && fact.needed !== BARE_NEEDED ? fact.needed : null);
  return { label: fact.value ?? 'Not specified', note };
}

/** The "Phase:" line of the protocol text the CRF Builder builds, or null when the phase is not known. */
export function protocolTextPhaseLine(i: PhaseDisplayInputs): string | null {
  const value = safeFact(i).value;
  return value ? `Phase: ${value}` : null;
}

/** The two documents the phase is resolved from besides the analysis. */
export interface PhaseContext { projectData: unknown; studyDesign: unknown }

/**
 * Read both documents through the caller's reader (getDoc in the CRF
 * Builder). A refused read is left out, never thrown, so the phase resolves
 * from what was read.
 */
export async function readPhaseContext(read: (which: 'project' | 'studyDesign') => Promise<unknown>): Promise<PhaseContext> {
  const safe = async (which: 'project' | 'studyDesign') => {
    try { return await read(which); } catch { return undefined; }
  };
  const [projectData, studyDesign] = await Promise.all([safe('project'), safe('studyDesign')]);
  return { projectData, studyDesign };
}

/**
 * The "--- Study Design ---" block of the CRF Builder's protocol text: type,
 * the resolved phase, blinding, randomization. The phase line appears
 * whenever the phase is known, even when the analysis has no studyDesign
 * object (round 2); no block when there is nothing to put in it.
 */
export function studyDesignTextLines(analysis: unknown, ctx: PhaseContext): string[] {
  const raw = obj(analysis).studyDesign;
  const sd = obj(raw);
  const phaseLine = protocolTextPhaseLine({ analysis, ...ctx });
  if (!(raw && typeof raw === 'object') && !phaseLine) return [];
  const lines = ['', '--- Study Design ---'];
  if (sd.type) lines.push(`Type: ${String(sd.type)}`);
  if (phaseLine) lines.push(phaseLine);
  if (sd.blinding) lines.push(`Blinding: ${String(sd.blinding)}`);
  if (sd.randomization) lines.push(`Randomization: ${String(sd.randomization)}`);
  return lines;
}
